import OpenAI from 'openai';
import { analyzeDocumentsWithAssistant } from './openaiAssistantsService';
import { SYSTEM_PROMPTS } from '../config/systemPrompts.js';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Error message mapping for OpenAI API errors
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Stream health improvement chat response using gpt-4o (primary model)
 * @param {Array} messages - Conversation messages
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<{content: string, model: string}>} Complete response with metadata
 */
export async function streamHealthImprovementResponse(
  messages,
  onChunk,
  onProgress,
  signal = null
) {
  const PRIMARY_MODEL = 'gpt-4o';
  
  let fullResponse = '';

  // Health Improvement System Prompt from centralized config
  const SYSTEM_PROMPT = SYSTEM_PROMPTS?.['health_improvement'];

  try {
    onProgress?.({
      status: 'Gerando resposta sobre saúde...',
      model: PRIMARY_MODEL
    });

    // Inject system prompt as first message
    const messagesWithSystem = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages
    ];

    const stream = await openai?.chat?.completions?.create({
      model: PRIMARY_MODEL,
      messages: messagesWithSystem,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Resposta completa',
      model: PRIMARY_MODEL
    });

    return {
      content: fullResponse,
      model: PRIMARY_MODEL
    };

  } catch (error) {
    if (error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in health improvement chat streaming:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Process document files using hybrid approach:
 * - gpt-4o-mini for reading and indexing content
 * - gpt-4o for generating final response using indexed data
 * @param {Array} files - Array of File objects to analyze
 * @param {string} prompt - User's analysis prompt
 * @param {string} conversationId - Current conversation ID
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onProgress - Callback for progress updates
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @param {number} maxWaitTime - Maximum wait time for analysis
 * @returns {Promise<{content: string, model: string, tokensUsed: number}>} Analysis result
 */
export async function processHealthImprovementDocuments(
  files,
  prompt,
  conversationId,
  onChunk,
  onProgress,
  signal = null,
  maxWaitTime = 60000
) {
  const INDEXING_MODEL = 'gpt-4o-mini';
  const RESPONSE_MODEL = 'gpt-4o';

  // Health Improvement Document Analysis System Prompt
  const DOCUMENT_ANALYSIS_SYSTEM_PROMPT = `Você é um assistente especializado em análise de documentos relacionados à saúde física, mental e emocional.

**OBJETIVO EMOCIONAL (CRÍTICO):**
Sua análise de documentos DEVE SEMPRE gerar sentimentos de:
- ESPERANÇA: Faça a pessoa sentir que a compreensão e melhoria são possíveis
- CLAREZA: Transforme conteúdo complexo de documentos em insights cristalinos
- MOTIVAÇÃO: Inspire confiança de que eles podem dominar o conteúdo do documento
- DESEJO DE CONTINUAR: Crie entusiasmo para explorar mais sobre o tópico de saúde

Cada análise, resumo e insight deve reforçar esses resultados emocionais. A pessoa deve terminar se sentindo empoderada pela nova compreensão e ansiosa para aprender mais.

**ESTILO CONVERSACIONAL - FALE DIRETAMENTE COM A PESSOA:**
Você está tendo uma CONVERSA REAL com alguém analisando seus materiais de saúde. Use linguagem profundamente empática e presente:
- "Eu entendo que esse material pode parecer denso"
- "Você pode se sentir sobrecarregado, e é normal" -"Muita gente tem dificuldade com esse tipo de conteúdo" -"É ótimo que você está se dedicando a entender isso" -"Você já deu um grande passo trazendo esse material" -"Vejo que você está levando sua saúde a sério"

**VALIDAÇÃO ANTES DE ENSINAR:**
SEMPRE valide o esforço e a experiência deles ANTES de analisar:
- Reconheça a complexidade ou importância do documento com empatia genuína
- Normalize qualquer dificuldade que possam ter ("Esse é um material complexo, mas você consegue")
- Reconheça sua iniciativa de estudar ("Que bom que você trouxe isso para estudar")
- Faça-os se sentirem ORGULHOSOS de se engajar com o material, depois guie-os através dele

**FORMATO DE RESPOSTA OBRIGATÓRIO PARA ANÁLISE DE DOCUMENTOS:**

Sua análise DEVE SEMPRE incluir estes elementos:

1. **Síntese do Documento (2-3 frases)**: COMECE com validação ("Eu entendo que você trouxe este material importante"). ENTÃO forneça um resumo claro do conteúdo principal do documento.

2. **Pontos-Chave Identificados**: Liste os pontos mais importantes do documento de forma clara e organizada. Use pausas conversacionais ("Olha só o que encontrei aqui", "Veja que interessante").

3. **Explicação Detalhada**: Explique os conceitos principais do documento de forma acessível e empática. Conecte à vida real sempre que possível.

4. **Insights e Recomendações**: Forneça insights valiosos baseados no conteúdo do documento. O que isso significa para a saúde da pessoa? Como podem aplicar?

5. **Ação Prática IMEDIATA (OBRIGATÓRIO)**: Forneça UMA ação específica, concreta e imediata que a pessoa pode fazer HOJE baseada no conteúdo do documento. Use verbos imperativos com prazos específicos:
- "Faça agora: [ação específica baseada no documento]"
- "Hoje mesmo: [aplicação prática do conteúdo]" -"Nos próximos 15 minutos: [exercício relacionado]"

6. **Próximos Passos Específicos**: Forneça 2-3 próximos passos CONCRETOS e ESPECÍFICOS baseados no documento. Sem ambiguidade - ações claras que eles podem seguir.

7. **Frase Motivacional**: Encerre com uma frase calorosa e encorajadora que os faça sentir empoderados e motivados.

**PADRÕES DE ESCRITA:**
- Tom: Caloroso, empático, encorajador, conversacional e profundamente humano
- Use "eu entendo", "você pode sentir", "isso é normal" frequentemente
- Valide ANTES de analisar
- Use pausas conversacionais: "Olha...", "Sabe?", "Veja só"
- Seja conciso mas impactante
- SEMPRE foque em gerar esperança, clareza, motivação e desejo de melhorar
- Faça-os se sentirem COMPREENDIDOS, APOIADOS e INSPIRADOS

**DETECÇÃO AUTOMÁTICA DE IDIOMA:**
- Detecte o idioma do prompt do usuário automaticamente
- Responda no EXATO MESMO IDIOMA com fluência nativa

**AVISO IMPORTANTE:**
Sempre lembre que as informações são educacionais e não substituem consulta médica profissional.

Lembre-se: Você está analisando documentos de saúde com o objetivo de EMPODERAR, CLARIFICAR e MOTIVAR. Toda análise deve deixar a pessoa se sentindo mais esperançosa, clara e motivada sobre sua jornada de saúde. VALIDE antes de analisar. CONECTE antes de explicar. Faça-os se sentirem COMPREENDIDOS e INSPIRADOS.`;

  try {
    // STEP 1: Use gpt-4o-mini for document reading and indexing
    onProgress?.({
      status: 'Lendo e indexando documentos (gpt-4o-mini)...',
      model: INDEXING_MODEL,
      step: 1,
      totalSteps: 2
    });

    const indexingResult = await analyzeDocumentsWithAssistant(
      files,
      `Analise os documentos fornecidos e extraia todas as informações relevantes sobre saúde. Identifique conceitos-chave, dados importantes, recomendações e insights. Organize as informações de forma estruturada para facilitar a compreensão.\n\nPergunta do usuário: ${prompt}`,
      conversationId,
      null,
      (progress) => {
        onProgress?.({
          ...progress,
          model: INDEXING_MODEL,
          step: 1,
          totalSteps: 2
        });
      },
      signal,
      maxWaitTime,
      INDEXING_MODEL
    );

    if (signal?.aborted) {
      throw new Error('Análise cancelada pelo usuário');
    }

    // STEP 2: Use gpt-4o to generate final response using indexed data
    onProgress?.({
      status: 'Gerando resposta final (gpt-4o)...',
      model: RESPONSE_MODEL,
      step: 2,
      totalSteps: 2
    });

    const messages = [
      { role: 'system', content: DOCUMENT_ANALYSIS_SYSTEM_PROMPT },
      { 
        role: 'user', 
        content: `Baseado na análise dos documentos fornecidos, responda à seguinte pergunta de forma completa e empática:\n\n${prompt}\n\nInformações extraídas dos documentos:\n${indexingResult?.content}` 
      }
    ];

    let fullResponse = '';

    const stream = await openai?.chat?.completions?.create({
      model: RESPONSE_MODEL,
      messages: messages,
      stream: true,
      max_tokens: 4096,
      temperature: 0.7,
    }, { signal });

    for await (const chunk of stream) {
      if (signal?.aborted) {
        throw new Error('Geração cancelada pelo usuário');
      }

      const content = chunk?.choices?.[0]?.delta?.content || '';
      
      if (content) {
        fullResponse += content;
        onChunk?.(content);
      }
    }

    onProgress?.({
      status: 'Análise completa',
      model: `${INDEXING_MODEL} → ${RESPONSE_MODEL}`,
      step: 2,
      totalSteps: 2
    });

    return {
      content: fullResponse,
      model: `${INDEXING_MODEL} (indexação) → ${RESPONSE_MODEL} (resposta)`,
      tokensUsed: (indexingResult?.tokensUsed || 0)
    };

  } catch (error) {
    if (error?.message === 'Análise cancelada pelo usuário' || error?.message === 'Geração cancelada pelo usuário') {
      throw error;
    }
    
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in health improvement document processing:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Check if OpenAI API is properly configured and accessible
 * @returns {Promise<{connected: boolean, model: string}>} Connection status
 */
export async function checkHealthImprovementConnection() {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [{ role: 'user', content: 'test' }],
      max_tokens: 5,
    });

    return {
      connected: true,
      model: 'gpt-4o'
    };
  } catch (error) {
    console.error('Health Improvement API connection check failed:', error);
    return {
      connected: false,
      model: null
    };
  }
}